import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'components/ui/panel';
import { Skeleton } from 'components/ui/skeleton';
import { formatGroupedNumber } from 'lib/dateFormat';
import type { ScoreBucket } from 'modules/analytics';
import { AdvancedUpgradeNotice } from './AdvancedUpgradeNotice';
import { FlatBarChart, type FlatBar } from './flat-bar-chart';

interface Props {
  distribution?: ScoreBucket[];
  isLoading: boolean;
  hasAdvanced: boolean;
}

const CHART_HEIGHT = 140;

/**
 * Graded results per 10% score band. Each test keeps its own passing score, so
 * there is no single pass mark to draw across the org-wide histogram.
 */
export const DistributionPanel: React.FC<Props> = ({ distribution, isLoading, hasAdvanced }) => {
  const { t } = useTranslation();
  const total = (distribution ?? []).reduce((sum, bucket) => sum + bucket.count, 0);
  const bars = useMemo<FlatBar[]>(
    () =>
      (distribution ?? []).map((bucket) => ({
        key: bucket.range,
        value: bucket.count,
        label: `${bucket.range.replace('-', '–')}%`,
        valueLabel: `${formatGroupedNumber(bucket.count)} ${t('pager_noun_results')}`,
      })),
    [distribution, t],
  );

  const renderBody = () => {
    if (!hasAdvanced) return <AdvancedUpgradeNotice />;
    if (isLoading) return <Skeleton className="w-full" height={CHART_HEIGHT} />;
    if (total === 0) {
      return (
        <p className="flex items-center justify-center text-[13px] text-muted-foreground" style={{ height: CHART_HEIGHT }}>
          {t('analytics_no_graded')}
        </p>
      );
    }
    return (
      <>
        <FlatBarChart bars={bars} height={CHART_HEIGHT} gap={6} aria-label={t('analytics_score_distribution')} />
        <div aria-hidden className="mt-1.5 flex justify-between text-[11.5px] tabular-nums text-muted-foreground">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </>
    );
  };

  return (
    <Panel
      title={t('analytics_score_distribution')}
      aside={
        hasAdvanced && total > 0 ? (
          <span className="tabular-nums">{`${formatGroupedNumber(total)} ${t('pager_noun_results')}`}</span>
        ) : undefined
      }
    >
      {renderBody()}
    </Panel>
  );
};

export default DistributionPanel;
